/**
 * Helix "last commanded" tracker - per-pedal record of the most recent
 * preset/snapshot change OPButtons itself successfully sent. Exists so a
 * DisplayItem bound to `source: 'helix'` has something to show.
 *
 * ## This is NOT live pedal state
 *
 * There is no MIDI-in feedback path from the Helix back to OPButtons today
 * (Windows dev-PC driver bug, untested on Mac - see the Helix MIDI-IN
 * project notes). A footswitch change made directly on the unit is
 * invisible here. This module only remembers what OPButtons itself fired,
 * which is why every consumer must label it "sent" / "last sent", never
 * "now playing" - see DisplayItem's doc comment in projectStore.ts for the
 * full framing.
 *
 * ## CORRECTION 2026-08-28: "untested on Mac" is now tested, and it WORKS
 *
 * The paragraph above remains true on the Windows dev PC and is left
 * standing as the record. On the Mac it is false. Measured 2026-08-28 via
 * OPBridge's `/monitor/snapshot` on the Mac: five footswitch preset changes
 * were observed live as `program: {"15": N}` with bank-select `cc: {"15":
 * {"0": 0, "32": 0}}` - channel key 15 is zero-indexed = MIDI channel 16,
 * exactly the DEC-149/150 wire format. OPBridge had auto-matched the port
 * (`"HELIX   "`, uppercase with trailing spaces on macOS) to
 * `deviceProfile: "line6-helix"` with `filteredCount: 0`, `denied: false`.
 *
 * So the limitation is PLATFORM-SPECIFIC, not universal - which matters
 * because the Mac is the primary bridge at gig time (DEC-147). A consumer
 * running against a Mac bridge can show real pedal state, including changes
 * the operator made with their foot that OPButtons never sent.
 *
 * This module is NOT yet wired to that feedback - it still only records our
 * own sends, so its own doc comment above still describes what it does. The
 * correction is about what is now POSSIBLE, and it is why the OPOverlay ask
 * at `OPApps/opbridge-opoverlay.md` proposes a `provenance` field
 * (`commanded` vs `observed`) rather than hard-coding the "sent" framing.
 *
 * Snapshot feedback confirmed the same day: `ccValues: {"15": {"69": 7,
 * "32": 0, "0": 0}}` with `program: {"15": 13}`. CC69 on channel 16 is the
 * snapshot selector, so BOTH halves - preset via Program Change, snapshot
 * via CC69 - are observable on macOS.
 *
 * Persists to localStorage (same idiom as `helix/library.ts`) so "last
 * sent" survives a reload instead of going blank on every page load.
 */

import { createSignal, type Accessor } from 'solid-js';

export interface HelixLastCommanded {
  kind: 'preset' | 'snapshot';
  /** preset only */
  setlist?: number;
  program?: number;
  presetName?: string;
  /** The snapshot value. For `kind: 'snapshot'` this is the tile's own
   *  target. For `kind: 'preset'` it's the OPTIONAL ride-along snapshot a
   *  PresetChangeItem can carry (DEC-151) - present only when the author
   *  set one, so a preset recall's Display face can show BOTH what preset
   *  AND what snapshot were sent, not just the preset. */
  snapshot?: number | 'next' | 'prev';
  /** Date.now() of the fire. */
  at: number;
}

const STORAGE_KEY = 'opbuttons.helix.lastCommanded.v1';

function load(): Record<string, HelixLastCommanded> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? parsed : {};
  } catch {
    return {};
  }
}

function persist(next: Record<string, HelixLastCommanded>): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* quota - memory only */ }
}

const [byPedalSignal, setByPedal] = createSignal<Record<string, HelixLastCommanded>>(load());

export const helixLastCommanded: Accessor<Record<string, HelixLastCommanded>> = byPedalSignal;

/** Called by PresetChange.tsx / SnapshotChange.tsx after a SUCCESSFUL fire
 *  (never on fault - a failed send didn't actually change anything on the
 *  pedal, so recording it would misreport "sent" for something that wasn't). */
export function recordHelixCommand(pedalId: string, cmd: HelixLastCommanded): void {
  setByPedal((prev) => {
    const next = { ...prev, [pedalId]: cmd };
    persist(next);
    return next;
  });
}
